import { useQuery } from "@tanstack/react-query";
import { queryClient } from "@/queryClient";
import {
  getAuthenticationStatus,
  getAuthenticationTypeRules,
  getAuthenticationTypes,
  getContactInformation,
  getEarningExpense,
  getIncomeInformation,
  getKnowYourCustomerSurveyFile,
  getKycCurrentForm,
  getKycOverview,
  getPersonalInformation,
  getPoliticalEffected,
  getPoliticalEffectedPeople,
} from "./services";
import { KycFormStep, type AuthenticationTypeSlug } from "./types";

export const useKycCurrentFormQuery = () =>
  useQuery({
    queryKey: ["knowYourCustomer", "currentForm"],
    queryFn: () => getKycCurrentForm(),
  });

export const fetchKycCurrentForm = () =>
  queryClient.fetchQuery({
    queryKey: ["knowYourCustomer", "currentForm"],
    queryFn: getKycCurrentForm,
  });

export const useKnowYourCustomerOverviewQuery = () =>
  useQuery({
    queryKey: ["knowYourCustomer", "overview"],
    queryFn: () => getKycOverview(),
  });

export const usePersonalInformationQuery = () =>
  useQuery({
    queryKey: ["knowYourCustomer", "personalInformation"],
    queryFn: () => getPersonalInformation(),
  });

export const prefetchPersonalInformation = async () => {
  await queryClient.prefetchQuery({
    queryKey: ["knowYourCustomer", "personalInformation"],
    queryFn: getPersonalInformation,
  });
};

export const useContactInformationQuery = () =>
  useQuery({
    queryKey: ["knowYourCustomer", "contactInformation"],
    queryFn: () => getContactInformation(),
  });

export const prefetchContactInformation = async () => {
  await queryClient.prefetchQuery({
    queryKey: ["knowYourCustomer", "contactInformation"],
    queryFn: getContactInformation,
  });
};

export const useIncomeInformationQuery = () =>
  useQuery({
    queryKey: ["knowYourCustomer", "incomeInformation"],
    queryFn: () => getIncomeInformation(),
  });

export const prefetchIncomeInformation = async () => {
  await queryClient.prefetchQuery({
    queryKey: ["knowYourCustomer", "incomeInformation"],
    queryFn: getIncomeInformation,
  });
};

export const useEarningExpenseQuery = () =>
  useQuery({
    queryKey: ["knowYourCustomer", "earningExpense"],
    queryFn: () => getEarningExpense(),
  });

export const prefetchEarningExpense = async () => {
  await queryClient.prefetchQuery({
    queryKey: ["knowYourCustomer", "earningExpense"],
    queryFn: getEarningExpense,
  });
};

export const usePoliticalEffectedQuery = () =>
  useQuery({
    queryKey: ["knowYourCustomer", "politicalEffected"],
    queryFn: () => getPoliticalEffected(),
  });

export const prefetchPoliticalEffected = async () => {
  await queryClient.prefetchQuery({
    queryKey: ["knowYourCustomer", "politicalEffected"],
    queryFn: getPoliticalEffected,
  });
};

export const usePoliticalEffectedPeopleQuery = () =>
  useQuery({
    queryKey: ["knowYourCustomer", "politicalEffectedPeople"],
    queryFn: () => getPoliticalEffectedPeople(),
  });

export const prefetchPoliticalEffectedPeople = async () => {
  await queryClient.prefetchQuery({
    queryKey: ["knowYourCustomer", "politicalEffectedPeople"],
    queryFn: getPoliticalEffectedPeople,
  });
};

export const prefetchKycForm = async (form: KycFormStep) => {
  switch (form) {
    case KycFormStep.PERSONAL_INFORMATION_FORM:
      await prefetchPersonalInformation();
      break;
    case KycFormStep.CONTACT_INFORMATION_FORM:
      await prefetchContactInformation();
      break;
    case KycFormStep.INCOME_INFORMATION_FORM:
      await prefetchIncomeInformation();
      break;
    case KycFormStep.EARNING_EXPENSE_FORM:
      await prefetchEarningExpense();
      break;
    case KycFormStep.POLITICAL_AFFECTED_FORM:
      await prefetchPoliticalEffected();
      break;
    case KycFormStep.POLITICAL_AFFECTED_PEOPLE_FORM:
      await prefetchPoliticalEffectedPeople();
      break;
  }
};

export const prefetchKycCurrentForm = async () => {
  const { currentForm } = await fetchKycCurrentForm();

  await prefetchKycForm(currentForm);
};

export const useAuthenticationTypesQuery = (
  authenticationPurposeType: string,
) =>
  useQuery({
    queryKey: ["knowYourCustomer", "authentication", authenticationPurposeType],
    queryFn: () => getAuthenticationTypes(authenticationPurposeType),
  });

export const useAuthenticationTypeRulesQuery = (
  authenticationPurpose: string,
  authenticationType: string,
) =>
  useQuery({
    queryKey: [
      "knowYourCustomer",
      "authentication",
      authenticationPurpose,
      authenticationType,
    ],
    queryFn: () =>
      getAuthenticationTypeRules(authenticationPurpose, authenticationType),
    enabled: !!authenticationType,
    staleTime: Infinity,
  });

export const useAuthenticationStatusQuery = (
  authenticationPurpose: string,
  authenticationTypeSlug: AuthenticationTypeSlug,
  sessionId?: string,
) =>
  useQuery({
    queryKey: [
      "knowYourCustomer",
      authenticationPurpose,
      authenticationTypeSlug,
      sessionId,
    ],
    queryFn: () =>
      getAuthenticationStatus(
        authenticationPurpose,
        authenticationTypeSlug,
        sessionId,
      ),
    enabled: !!sessionId,
    refetchInterval: 2000,
  });

export const useKycFileQuery = () =>
  useQuery({
    queryKey: ["knowYourCustomer", "file"],
    queryFn: getKnowYourCustomerSurveyFile,
    staleTime: Infinity,
    enabled: false,
  });
